import {
  cn,
  Menu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
  Separator,
} from "@forge/ui";
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import {
  Activity,
  Check,
  ChevronsUpDown,
  ChartLine,
  CircleDollarSign,
  CreditCard,
  Flame,
  LayoutDashboard,
  Link2,
  LogOut,
  Menu as MenuIcon,
  MessageCircleQuestion,
  MessageSquare,
  Scale,
  Moon,
  Package,
  Settings,
  ShoppingBag,
  ShoppingCart,
  Sun,
  Tags,
  Truck,
} from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { canAccessNav, canAccessWebPath } from "../lib/areas";
import { useChannels } from "../lib/channel";
import { loadSession, meQueryOptions } from "../lib/auth";
import { NotificationBell } from "../components/notification-bell";
import { mobileTitleForPath } from "../lib/mobile-title";
import { SITE_NAME } from "../lib/site";

export const Route = createFileRoute("/_app")({
  beforeLoad: async ({ context, location }) => {
    try {
      const me = await loadSession(context.queryClient);
      if (
        "role" in me &&
        !canAccessWebPath({
          role: me.role,
          areas: "areas" in me ? me.areas : null,
          pathname: location.pathname,
        })
      ) {
        throw redirect({ to: "/home" });
      }
    } catch (error) {
      if (error && typeof error === "object" && "to" in error) throw error;
      throw redirect({ to: "/login" });
    }
  },
  component: AppLayout,
});

const ACCESS_BANNER: Record<
  string,
  { title: string; description: string; href?: "/billing" }
> = {
  past_due: {
    title: "Pagamento em atraso",
    description:
      "A sincronização está pausada. Regularize para voltar a importar e enviar estoque.",
    href: "/billing",
  },
  trial_expired: {
    title: "Trial encerrado",
    description:
      "Assine um plano para voltar a sincronizar. Pedidos e anúncios já importados continuam visíveis.",
    href: "/billing",
  },
  canceled: {
    title: "Assinatura encerrada",
    description:
      "A sincronização está pausada. Assine de novo para retomar.",
    href: "/billing",
  },
  tenant_suspended: {
    title: "Organização suspensa",
    description:
      "A sincronização está pausada. Fale com o suporte se isso for um engano.",
  },
  tenant_canceled: {
    title: "Organização encerrada",
    description: "Esta organização não sincroniza mais.",
  },
};

function AccessBanner({ reason }: { reason: string | null }) {
  const copy = ACCESS_BANNER[reason ?? ""] ?? {
    title: "Sincronização pausada",
    description: "A sincronização desta organização está pausada.",
    href: "/billing" as const,
  };

  return (
    <div className="mb-5 rounded-surface border border-warning/30 bg-warning-subtle px-4 py-3">
      <p className="text-[0.8125rem] font-semibold text-ink">{copy.title}</p>
      <p className="mt-0.5 text-2xs text-ink-muted">{copy.description}</p>
      {copy.href && (
        <Link
          to={copy.href}
          className="mt-2 inline-block text-2xs font-medium text-ember-strong hover:underline"
        >
          Ver plano →
        </Link>
      )}
    </div>
  );
}

type NavItem = {
  to:
    | "/home"
    | "/orders"
    | "/fulfillment"
    | "/questions"
    | "/messages"
    | "/claims"
    | "/listings"
    | "/stock"
    | "/purchases"
    | "/profit"
    | "/dashboard"
    | "/connections";
  label: string;
  icon: typeof LayoutDashboard;
  requires?: "questions" | "messages" | "claims";
};

const NAV_GROUPS: Array<{ label?: string; items: readonly NavItem[] }> = [
  {
    items: [{ to: "/home", label: "Hoje", icon: LayoutDashboard }],
  },
  {
    label: "Operação",
    items: [
      { to: "/orders", label: "Pedidos", icon: ShoppingBag },
      { to: "/fulfillment", label: "Expedição", icon: Truck },
      { to: "/questions", label: "Perguntas", icon: MessageCircleQuestion, requires: "questions" },
      { to: "/messages", label: "Mensagens", icon: MessageSquare },
      { to: "/claims", label: "Reclamações", icon: Scale },
    ],
  },
  {
    label: "Catálogo",
    items: [
      { to: "/listings", label: "Anúncios", icon: Tags },
      { to: "/stock", label: "Estoque", icon: Package },
      { to: "/purchases", label: "Compras", icon: ShoppingCart },
      { to: "/profit", label: "Lucro", icon: CircleDollarSign },
      { to: "/dashboard", label: "Painel", icon: ChartLine },
    ],
  },
  {
    label: "Conta",
    items: [{ to: "/connections", label: "Conexões", icon: Link2 }],
  },
];

const MOBILE_DOCK: readonly NavItem[] = [
  { to: "/home", label: "Hoje", icon: LayoutDashboard },
  { to: "/questions", label: "Perguntas", icon: MessageCircleQuestion, requires: "questions" },
  { to: "/messages", label: "Mensagens", icon: MessageSquare, requires: "messages" },
  { to: "/claims", label: "Reclamações", icon: Scale, requires: "claims" },
];

function navItemActive(to: NavItem["to"], pathname: string) {
  if (to === "/home") return pathname === "/home";
  if (to === "/listings") {
    return (
      pathname === "/listings" ||
      pathname.startsWith("/listings/") ||
      pathname === "/demand"
    );
  }
  return pathname === to || pathname.startsWith(`${to}/`);
}

function initialsOf(name: string | null | undefined): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase() || "?";
}

function useTheme() {
  const [dark, setDark] = useState(() =>
    document.documentElement.classList.contains("dark"),
  );
  return {
    dark,
    toggle: () => {
      const next = !dark;
      setDark(next);
      document.documentElement.classList.toggle("dark", next);
      localStorage.setItem("fc-theme", next ? "dark" : "light");
    },
  };
}

function AppLayout() {
  const { data: me } = useSuspenseQuery(meQueryOptions);
  const channels = useChannels();
  const { dark, toggle } = useTheme();
  const router = useRouter();
  const queryClient = useQueryClient();

  async function logout() {
    await api.auth.logout.post();
    queryClient.clear();
    await router.navigate({ to: "/login" });
  }

  const switchTenant = useMutation({
    mutationFn: async (tenantId: string) =>
      api.auth["switch-tenant"].post({ tenantId }),
    onSuccess: async () => {
      queryClient.clear();
      await router.invalidate();
    },
  });

  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const wideLayout = useRouterState({
    select: (s) => s.matches.at(-1)?.staticData?.layout === "wide",
  });
  const tenants = ("tenants" in me ? me.tenants : null) ?? [];
  const [mobileNav, setMobileNav] = useState(false);
  const [desktop, setDesktop] = useState(true);

  useEffect(() => {
    const media = window.matchMedia("(min-width: 768px)");
    const sync = () => setDesktop(media.matches);
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);

  return (
    <div className="flex min-h-dvh min-w-0 overflow-x-hidden bg-surface">
      <button
        type="button"
        aria-label="Fechar menu"
        tabIndex={mobileNav ? 0 : -1}
        className={cn(
          "fixed inset-0 z-20 bg-ink/20 md:hidden print:hidden",
          "transition-opacity duration-200 ease-swift",
          mobileNav
            ? "opacity-100"
            : "pointer-events-none opacity-0",
        )}
        onClick={() => setMobileNav(false)}
      />
      <aside
        aria-hidden={!desktop && !mobileNav}
        className={cn(
          "fixed inset-y-0 left-0 z-30 flex w-52 flex-col border-r border-line bg-surface-raised print:hidden",
          "transition-transform duration-200 ease-swift md:translate-x-0 md:transition-none",
          mobileNav
            ? "translate-x-0"
            : "-translate-x-full pointer-events-none md:pointer-events-auto",
        )}
      >
        <div className="flex items-center gap-2 px-4 py-3.5">
          <div className="flex size-6 items-center justify-center rounded-control bg-ember text-ink-inverse">
            <Flame className="size-3.5" />
          </div>
          <span className="text-[0.8125rem] font-semibold tracking-tight text-ink">
            {SITE_NAME}
          </span>
        </div>

        <Separator />

        <nav className="flex flex-1 flex-col overflow-y-auto p-2">
          {NAV_GROUPS.map((group, index) => {
            const items = group.items.filter(
              (item) =>
                canAccessNav({
                  role: "role" in me ? me.role : null,
                  areas: "areas" in me ? me.areas : null,
                  to: item.to,
                }) &&
                (!item.requires ||
                  channels.loading ||
                  channels.can(item.requires)),
            );
            if (items.length === 0) return null;
            return (
            <div
              key={group.label ?? "hoje"}
              className={cn("flex flex-col gap-0.5", index > 0 && "mt-3")}
            >
              {group.label && (
                <p className="px-2.5 pb-1 text-[0.625rem] font-medium uppercase tracking-[0.08em] text-ink-faint">
                  {group.label}
                </p>
              )}
              {items.map((item) => {
                const active = navItemActive(item.to, pathname);
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setMobileNav(false)}
                    className={cn(
                      "flex items-center gap-2.5 rounded-control px-2.5 py-1.5",
                      "text-[0.8125rem] font-medium text-ink-secondary",
                      "transition-[background-color,color,transform] duration-100 ease-swift",
                      "[@media(hover:hover)_and_(pointer:fine)]:hover:bg-surface-sunken",
                      "[@media(hover:hover)_and_(pointer:fine)]:hover:text-ink",
                      "active:scale-[0.97]",
                      active &&
                        "bg-ember-subtle text-ember-strong hover:bg-ember-subtle hover:text-ember-strong",
                    )}
                  >
                    <item.icon className="size-4 shrink-0" />
                    {item.label}
                  </Link>
                );
              })}
            </div>
            );
          })}
        </nav>

        <div className="flex flex-col gap-2 border-t border-line p-2">
          {"impersonated" in me && me.impersonated && (
            <p className="rounded-control border border-warning/30 bg-warning-subtle px-2 py-1 text-2xs text-warning">
              Sessão de suporte ativa
            </p>
          )}
          <div className="flex items-center gap-0.5">
          <div className="min-w-0 flex-1">
          <Menu>
            <MenuTrigger
              render={
                <button
                  type="button"
                  className={cn(
                    "flex w-full min-w-0 items-center gap-2.5 rounded-control p-2 text-left",
                    "transition-colors duration-100 [@media(hover:hover)_and_(pointer:fine)]:hover:bg-surface-sunken",
                    "data-popup-open:bg-surface-sunken",
                  )}
                >
                  <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-ember-subtle text-2xs font-semibold text-ember-strong">
                    {initialsOf(me.user?.name)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-2xs font-medium text-ink">
                      {me.user?.name}
                    </span>
                    <span className="block truncate text-2xs text-ink-muted">
                      {me.tenant?.name}
                    </span>
                  </span>
                  <ChevronsUpDown className="size-3.5 shrink-0 text-ink-muted" />
                </button>
              }
            />
            <MenuContent side="top" align="start" className="w-52">
              <div className="px-2 py-1.5">
                <p className="truncate text-[0.8125rem] font-medium text-ink">
                  {me.user?.name}
                </p>
                <p className="truncate text-2xs text-ink-muted">
                  {me.user?.email}
                </p>
              </div>
              <MenuSeparator />
              <MenuItem
                render={
                  <Link to="/settings">
                    <Settings />
                    Configurações
                  </Link>
                }
              />
              <MenuItem
                render={
                  <Link to="/billing">
                    <CreditCard />
                    Plano
                  </Link>
                }
              />
              <MenuItem
                render={
                  <Link to="/activity">
                    <Activity />
                    Atividade
                  </Link>
                }
              />
              <MenuItem onClick={toggle}>
                {dark ? <Sun /> : <Moon />}
                {dark ? "Tema claro" : "Tema escuro"}
              </MenuItem>
              {tenants.length > 1 && (
                <>
                  <MenuSeparator />
                  <p className="px-2 pb-0.5 pt-1 text-2xs text-ink-faint">
                    Organizações
                  </p>
                  {tenants.map((tenant) => (
                    <MenuItem
                      key={tenant.id}
                      onClick={() => {
                        if (tenant.id !== me.tenant?.id) {
                          switchTenant.mutate(tenant.id);
                        }
                      }}
                    >
                      <span className="flex-1 truncate">{tenant.name}</span>
                      {tenant.id === me.tenant?.id && (
                        <Check className="text-ember" />
                      )}
                    </MenuItem>
                  ))}
                </>
              )}
              <MenuSeparator />
              <MenuItem onClick={logout}>
                <LogOut />
                Sair
              </MenuItem>
            </MenuContent>
          </Menu>
          </div>
          <NotificationBell side="top" align="end" />
          </div>
        </div>
      </aside>

      <main className="min-w-0 flex-1 overflow-x-hidden pb-[calc(4.25rem+env(safe-area-inset-bottom))] md:ml-52 md:pb-0 print:ml-0 print:pb-0">
        <div className="flex items-center gap-2 border-b border-line px-3 py-2.5 md:hidden print:hidden">
          <button
            type="button"
            aria-label="Abrir menu"
            className="rounded-control p-2 text-ink-secondary transition-[transform,background-color] duration-100 ease-swift [@media(hover:hover)_and_(pointer:fine)]:hover:bg-surface-sunken active:scale-[0.97]"
            onClick={() => setMobileNav(true)}
          >
            <MenuIcon className="size-4" />
          </button>
          <span className="min-w-0 flex-1 truncate text-[0.8125rem] font-semibold tracking-tight text-ink">
            {mobileTitleForPath(pathname)}
          </span>
          <NotificationBell side="bottom" align="end" />
        </div>
        <div
          className={cn(
            "mx-auto min-w-0 p-4 md:p-6 print:max-w-none print:p-0",
            wideLayout ? "max-w-[100rem]" : "max-w-6xl",
          )}
        >
          {"access" in me &&
            me.access?.syncBlocked &&
            pathname !== "/billing" && (
              <AccessBanner reason={me.access.blockReason} />
            )}
          <Outlet />
        </div>
      </main>

      <nav className="fixed inset-x-0 bottom-0 z-10 flex border-t border-line bg-surface-raised px-2 pb-[env(safe-area-inset-bottom)] pt-1 print:hidden md:hidden">
        {MOBILE_DOCK.filter(
          (item) =>
            canAccessNav({
              role: "role" in me ? me.role : null,
              areas: "areas" in me ? me.areas : null,
              to: item.to,
            }) &&
            (!item.requires || channels.loading || channels.can(item.requires)),
        ).map((item) => {
          const active = navItemActive(item.to, pathname);
          return (
            <Link
              key={item.to}
              to={item.to}
              className={cn(
                "flex flex-1 flex-col items-center gap-0.5 rounded-control py-1.5",
                "text-[0.625rem] font-medium text-ink-muted",
                "transition-[color,transform] duration-100 ease-swift active:scale-[0.97]",
                "[@media(hover:hover)_and_(pointer:fine)]:hover:text-ink",
                active && "text-ember-strong",
              )}
            >
              <item.icon className="size-5" />
              {item.label}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
